
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { ROUTER } from '@/router/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { functions } from '@/firebase';
import {
	TEAM_USER_ROLE_PERMISSION,
	TEAM_USER_ROLE_PERMISSION_PASSING_VAL,
} from '@run-diffusion/shared';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import PluginCard from '@/components/Plugins/PluginCard.vue';
import BetaCallout from '@/components/Plugins/BetaCallout.vue';
import PluginsCircuitBg from '@/components/Plugins/PluginsCircuitBg.vue';
import ApiIntegrationsSection from '@/components/Plugins/ApiIntegrationsSection.vue';
import OpenSourceApiSection from '@/components/Plugins/OpenSourceApiSection.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import TokenRevealDialog from '@/views/Team/components/TokenRevealDialog.vue';
import { buildPluginsCatalog, PluginCardData } from '@/components/Plugins/pluginsCatalog';
import { buildPluginsFeedbackFormUrl } from '@/components/Plugins/pluginsFeedback';

export default Vue.extend({
	name: 'PluginsPage',
	mixins: [
		TeamUserMixin,
	],
	components: {
		PluginCard,
		BetaCallout,
		PluginsCircuitBg,
		ApiIntegrationsSection,
		OpenSourceApiSection,
		EmptyState,
		TokenRevealDialog,
	},
	metaInfo () {
		return {
			title: `${this.pluginsSurfaceLabel} | RunDiffusion`,
		};
	},
	data () {
		return {
			loadingReportingToken: false,
			reportingTokenModalOpen: false,
			reportingTokenJustGenerated: null as string | null,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
		]),
		...mapGetters([
			'displayLabels',
		]),
		// The full catalog, before permissions. `visiblePlugins` is what renders.
		plugins (): PluginCardData[] {
			return buildPluginsCatalog(this.user, this.team, this.displayLabels);
		},
		/**
		 * The plugins this role may actually use.
		 *
		 * A plugin the role is not granted is HIDDEN rather than shown-and-disabled.
		 * A disabled card with a "contact your admin" tooltip advertises a product
		 * decision the member cannot act on, and on a locked-down Enterprise role
		 * that was most of the page.
		 *
		 * `isPluginKindEnabled` already returns true for RD admins, personal
		 * accounts, and non-enterprise teams (ADR-0021), so in practice this only
		 * removes cards for an Enterprise member whose role omits the kind.
		 */
		visiblePlugins (): PluginCardData[] {
			return this.plugins.filter((plugin: PluginCardData) => {
				// 'coming-soon' is gated too. It is still an announcement of a plugin
				// kind, so a company that has not enabled that kind should not have it
				// advertised to their members — including as something to look forward
				// to and then ask for.
				//
				// While permissions are still resolving, fall back to the permission's
				// non-passing value (the same stance TeamUserMixin takes for every other
				// team permission). USE_PLUGINS passes when ON, so that resolves to hide:
				// a member who does have access sees the cards a moment later, whereas
				// showing first would briefly advertise plugin kinds to companies that
				// never enabled them. useNonPassingVal is the mixin's loading signal.
				if (this.getTeamPermissionOverridePrechecks.useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.USE_PLUGINS];
				// No USE_PLUGINS at all (e.g. an enterprise admin who came for the API
				// section) hides every plugin; otherwise hide the specific kinds the
				// role omits.
				return this.hasUsePluginsPermission && this.isPluginKindEnabled(plugin.kind);
			});
		},
		/**
		 * Whether any plugin this role can see is still in beta. Gates the beta
		 * callout, which speaks for the cards under it: a role granted only
		 * launched plugins (Revit, say) would otherwise be told its integrations
		 * are in beta when none of the ones it can see is.
		 */
		hasBetaPlugins (): boolean {
			return this.visiblePlugins.some((plugin: PluginCardData) => plugin.status === 'beta');
		},
		pluginsFeedbackUrl (): string {
			return buildPluginsFeedbackFormUrl(this.user, this.team);
		},
		/**
		 * The account being acted as. `team` stays bound after switching to
		 * personal, so its presence says nothing — `runnitsOwnerSelection` is the
		 * authority, same as the account switcher.
		 */
		isTeamSelected (): boolean {
			return (
				this.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
				&& !!this.team?.id
			);
		},
		/**
		 * Who sees the API section.
		 *
		 * On a team, MANAGE_API_TOKENS gates it entirely: a regular member sees no
		 * API section at all, and cannot create even their own company-scoped
		 * token. Deliberate — company tokens spend the organization's balance, so
		 * minting them is an admin action. A member who needs one asks an admin,
		 * who creates it against their name so the work is still attributed to
		 * them. The docs say this; keep the two in step if this ever loosens.
		 *
		 * On a personal account the section is always shown. The permission is a
		 * COMPANY role permission and resolves false for everyone acting
		 * personally (see getCompanyPermissionOverridePrechecks), so gating on it
		 * unconditionally would hide a user's OWN token from them.
		 */
		canViewApiSection (): boolean {
			return !this.isTeamSelected || this.hasManageApiTokensPermission;
		},
		/**
		 * Whether NOTHING on this page has content for this account.
		 *
		 * Every section below the header gates independently, and a role can fail
		 * all three at once: no plugin kinds, no USE_RD, no MANAGE_API_TOKENS. That
		 * left a title and a subtitle sitting above an empty panel, which reads as a
		 * page that failed to load rather than one the account cannot use.
		 *
		 * Cannot flash mid-load: `visiblePlugins` returns the whole catalog while
		 * permissions are still resolving, so this is false until the real answer
		 * has arrived.
		 */
		hasNothingToShow (): boolean {
			return (
				!this.visiblePlugins.length
				&& !this.hasUseRdPermission
				&& !this.canViewApiSection
			);
		},
		pageSubtitle (): string {
			return 'Bring RunDiffusion into the creative tools you already use, and connect usage data to your reporting stack via the API.';
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		goToAuthDevices () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.AUTH_DEVICES });
		},
		/**
		 * Both mint a secret, so both funnel through here: one reveal dialog, one
		 * error path, and the token value never lands anywhere but the dialog.
		 */
		async mintToken (action: 'create' | 'roll', payload: object, onDone?: (ok: boolean) => void) {
			// Only a create drives this flag. It is passed to the card as
			// `loadingToken` and spins the Create token button, so setting it for a
			// roll put the spinner on the button that was not doing the work. A
			// roll's progress belongs to the row it came from, which tracks its own
			// `busySecretId`.
			const isCreate: boolean = action === 'create';
			if (isCreate) this.loadingReportingToken = true;
			try {
				const fn = functions.httpsCallable('managePublicApiAccessToken');
				const result = await fn({ action, ...payload });
				const data = result?.data as { success?: boolean; token?: string; message?: string };
				if (data?.success && data?.token) {
					this.reportingTokenJustGenerated = data.token;
					this.reportingTokenModalOpen = true;
					if (onDone) onDone(true);
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: data?.message || 'Failed to generate API token',
						show: true,
					});
					if (onDone) onDone(false);
				}
			} catch (e: any) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: e?.message || 'Failed to generate API token',
					show: true,
				});
				if (onDone) onDone(false);
			} finally {
				if (isCreate) this.loadingReportingToken = false;
			}
		},
		/**
		 * `companyId` comes from the card, which reads the selected account rather
		 * than whether `team.company` happens to be populated: `team` stays bound
		 * after switching to personal, so testing the company alone would scope a
		 * personal token to the whole organization.
		 */
		async onCreateToken (payload: { companyId: string | null; label: string | null; userId: string | null; onDone: (ok: boolean) => void }) {
			await this.mintToken(
				'create',
				{
					...(payload.companyId ? { companyId: payload.companyId } : {}),
					...(payload.label ? { label: payload.label } : {}),
					// Omitted when creating your own, so the callable defaults the
					// owner to the caller rather than being handed their own uid.
					...(payload.userId ? { userId: payload.userId } : {}),
				},
				payload.onDone,
			);
		},
		async onRollToken (payload: { secretId: string; onDone: (ok: boolean) => void }) {
			await this.mintToken(
				'roll',
				{ secretId: payload.secretId },
				// `ok` is forwarded rather than dropped: it is what tells the row
				// whether to refetch the list, and it clears the row's loading state
				// on both outcomes.
				(ok: boolean) => payload.onDone(ok),
			);
		},
	},
});
